export type Provider = 'aws' | 'azure' | 'oci' | 'core42' | 'alibaba' | 'gcp' | 'nebius' | 'mistral' | 'telnyx' | 'snowflake' | 'cloudflare' | 'databricks' | 'fireworks' | 'deepinfra' | 'gemini' | 'hfendpoints' | 'watsonx' | 'xai' | 'together' | 'crusoe' | 'huawei' | 'groq' | 'openai' | 'anthropic' | 'fireworks' | 'openai' | 'anthropic'

export interface GpuOption {
  provider: Provider
  region: string
  name: string
  gpu_model: string
  gpu_count: number
  gpu_mem_gb: number
  price_usd: number
  pricing_model: string
  total_vram_gb: number
  usd_per_gpu_hour: number
  usd_per_month_24x7: number
  /** one of them running every hour of the period asked for (Fit's Time step) */
  usd_over_period?: number
  /** Present only when the VM gets a slice of a GPU rather than a whole one. */
  gpu_fraction?: string
  note?: string
  /** What joins the instance's GPUs: nvlink (incl. NVSwitch), infinity-fabric, or pcie. */
  interconnect: 'nvlink' | 'infinity-fabric' | 'pcie'
  /** With fits_model: true when the model needs more memory than one GPU holds. */
  sharded?: boolean
  /** Both present only when the query was filtered by fits_model. */
  required_vram_gb?: number
  headroom_gb?: number
  fetched_at: number
}

export interface TokenPrice {
  provider: Provider
  region: string
  name: string
  unit: string
  price_usd: number
  pricing_model: string
  service_tier: string | null
  raw_unit: string
  raw_price: number
  meta: Record<string, unknown>
}

export interface ReservedPrice {
  provider: Provider
  region: string
  name: string
  price_usd: number
  raw_unit: string
  usd_per_month_24x7: number
  meta: Record<string, unknown>
}

export interface ModelSummary {
  canonical: string
  label: string
  /** Family for grouping: Meta, Qwen, OpenAI ... or "Other" for the long tail. */
  family: string
  /** The raw organisation slug, kept so "Other" models stay attributable. */
  organisation: string | null
  open_weights: boolean
  params_billions: number | null
  active_params_billions: number | null
  providers: Provider[]
}

export interface CloudPrice {
  usd_per_1m: number
  name: string
  region: string
  service_tier: string | null
  blended: boolean
  assumption: string | null
  /** the page the figure was read from, and the vendor's own number and unit as read */
  source?: string | null
  raw?: string | null
  /** the precision the vendor says it serves the model at (Nebius: "fp4"); null where it does not say */
  quantization?: string | null
}

/** A region choice per vendor: "in-country", "any" (cheapest region), or a region id. */
export type RegionChoices = Record<Provider, string>
export const IN_COUNTRY = 'in-country'
/** The region choice for a country: the home market is plain "in-country", any other "in-country:DE". */
export const countryChoice = (code: string, home = 'AE') => (code === home ? IN_COUNTRY : `${IN_COUNTRY}:${code}`)
/** The country a region choice names, or null when it names none. */
export const choiceCountry = (choice: string | undefined, home = 'AE'): string | null =>
  choice === IN_COUNTRY ? home : choice?.startsWith(`${IN_COUNTRY}:`) ? choice.slice(IN_COUNTRY.length + 1) : null
export const ANY_REGION = 'any'

export interface ModelComparison {
  canonical: string
  label: string
  open_weights: boolean
  params_billions: number | null
  active_params_billions: number | null
  pricing_model: string
  regions: RegionChoices
  clouds: {
    provider: Provider
    prices: Record<string, CloudPrice>
    /** the region the pair of prices came from; the choice it was made under */
    region?: string | null
    region_choice: string
    region_label: string
    /** why there is no price: not sold in the chosen region */
    unavailable: string | null
    /** held to a place, the price is a Global or Data Zone deployment: billed there, processed elsewhere */
    residency_note?: string | null
    /** when this vendor's serving price list was collected */
    collected_at?: number | null
    /** Where this cloud sells the model. OCI's come from Oracle's docs. */
    regions: string[]
    region_count: number
    region_source: 'price-list' | 'oracle-docs'
  }[]
  missing_from: Provider[]
  self_hosting?: string
}

export interface Freshness {
  providers: {
    provider: Provider
    snapshot_id: number
    records: number
    age_hours: number
    collected_at: number
    stale: boolean
    /** an administrator is serving an earlier snapshot for this vendor */
    pinned?: boolean
    latest_snapshot_id?: number
    pinned_by?: string
    pinned_at?: number
  }[]
  stale: boolean
  stale_after_hours: number
  /** the scheduled refresh: its period, and when it next runs (epoch seconds) */
  refresh_interval_hours: number | null
  next_refresh_at: number | null
  /** the administrator's switch for the scheduled refreshes */
  auto_refresh?: boolean
}

export type RetentionUnit = 'snapshots' | 'days' | 'weeks' | 'months' | 'years'
/** What a retention rule would delete (or did): per vendor and in total. */
export interface RetentionPlan {
  keep: number
  unit: RetentionUnit
  cutoff: number | null
  snapshots: number
  rows: number
  by_provider: Record<string, { snapshots: number; kept: number; rows: number; oldest_kept: number | null }>
  saved?: { keep: number; unit: RetentionUnit }
  database_bytes?: number | null
  deleted?: number
  compacted_bytes?: number
}

export interface ManagedEstimate {
  input_usd: number
  output_usd: number
  total_usd: number
  days: number
}

export interface SelfHostedCase {
  factor: number
  tokens_per_second: number
  monthly_usd: number
  monthly_output_tokens_capacity: number
  effective_usd_per_1m_output_tokens: number
  assumptions: Assumptions
}

export interface Assumptions {
  tokens_per_second: number
  utilization: number
  instances: number
  hours_per_month: number
}

export interface SelfHostedEstimate {
  monthly_usd: number
  monthly_output_tokens_capacity: number
  effective_usd_per_1m_output_tokens: number
  assumptions: Assumptions
  sensitivity: SelfHostedCase[]
}

export interface BreakevenEstimate {
  instance_monthly_usd: number
  blended_usd_per_1m_output: number
  breakeven_output_tokens_per_month: number
  breakeven_output_tokens_per_second_avg: number
  assumptions: { input_output_ratio: number; hours_per_month: number }
  note: string
}

export interface VramEstimate {
  weights_gb: number
  recommended_min_vram_gb: number
  assumption: string
}

export type ChatEvent =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; id: string; name: string; output?: unknown; error?: string }
  | { type: 'usage'; input_tokens: number; output_tokens: number; model: string }
  | { type: 'budget'; used: number; budget: number }
  | { type: 'done'; stop_reason: string }
  | { type: 'error'; message: string }


export interface CatalogModel {
  canonical: string
  hf_id: string | null
  /** Family for grouping: Meta, Qwen, OpenAI ... or "Other" for the long tail. */
  family: string
  organisation: string | null
  params_b: number | null
  native_precision: string | null
  license: string | null
  open_weights: boolean
  downloads: number | null
  /** false when no cloud sells it per token — self-hosting only. */
  sold_by_cloud: boolean
}

/** Several identical servers for a model no one server holds: NVLink or Infinity Fabric inside each,
 *  and a network between servers that the vendor documents. Priced as all of them. */
export interface ClusterOption {
  provider: Provider
  region: string
  name: string
  gpu_model: string
  gpu_count: number
  gpu_mem_gb: number
  interconnect: 'nvlink' | 'infinity-fabric' | 'pcie'
  servers: number
  server_vram_gb: number
  total_vram_gb: number
  /** what the vendor calls the network between servers, and the page that documents it */
  cluster_network: string
  cluster_source: string
  usd_per_hour_per_server: number
  /** all the servers, per hour */
  price_usd: number
  usd_per_month_24x7: number
  usd_over_period?: number
  required_vram_gb: number
  headroom_gb: number
}

export interface VendorFit {
  provider: Provider
  region_choice: string
  region_label: string
  fits: GpuOption[]
  total_fitting: number
  /** Multi-GPU boxes with enough memory in total but only PCIe between the GPUs: not offered. */
  left_out_pcie: string[]
  /** Regions with at least one instance the model fits on. */
  regions: string[]
  cheapest_usd_per_hour: number | null
  cheapest_instance: string | null
  largest_available_vram_gb: number
  /** How far short the biggest box on this cloud is, when nothing fits. */
  shortfall_gb: number | null
  /** Several servers of one type that hold the model together, cheapest first: the only way where no one
   *  server holds it, and listed beside the single servers where one does. */
  clusters?: ClusterOption[]
}

export interface ModelFit {
  canonical: string
  /** the period asked for, when one was */
  period?: { months: number; hours: number; basis: string }
  label: string
  params_billions: number
  precision: string
  regions: RegionChoices
  native_precision: string | null
  /** GB of weight files the repo publishes; set only where the precision is not known */
  published_weights_gb?: number | null
  required_vram_gb: number
  overhead_fraction: number
  /** where the memory goes: the weights, the KV cache of the use case, a working margin */
  memory: MemoryBreakdown
  arch: ModelArch | null
  vendors: VendorFit[]
  fits_anywhere: boolean
  /** the cheapest way to hold it on several servers, on any cloud */
  cheapest_cluster?: ClusterOption | null
}

export interface MemoryBreakdown {
  /** "architecture": worked out from the model's layers and heads; "flat": the percentage allowance */
  basis: 'architecture' | 'flat'
  weights_gb: number
  kv_cache_gb: number | null
  working_gb: number
  required_gb: number
  context_tokens: number | null
  context_capped?: boolean
  concurrency: number | null
  kv_bytes_per_token?: number | null
}

/** What a model is made of (pricing/arch.py). */
export interface ModelArch {
  kind: 'dense' | 'moe' | null
  layers: number | null
  kv_heads: number | null
  head_dim: number | null
  mla_dim: number | null
  full_layers: number | null
  sliding_window: number | null
  max_context: number | null
  experts: number | null
  experts_per_token: number | null
  vision: number | null
  model_type: string | null
  source: string | null
}

export interface UseCase {
  id: string
  label: string
  context_tokens: number
  concurrency: number
  input_output_ratio: number
  note: string
  /** what one unit of work costs in tokens, assumed (the Calculator's "Set from tasks") */
  tasks: { unit: string; prompt_tokens: number; answer_tokens: number; calls_per_task: number; reasoning_share: number; retry_rate: number }
}


export interface SourceEndpoint { role: string; url: string; note: string }
export interface VendorSource {
  provider: string
  credentials: string
  endpoints: SourceEndpoint[]
}

export interface Snapshot {
  id: number
  provider: Provider
  created_at: number
  record_count: number
  status: string
  is_current: boolean
  pinned?: boolean
}

export interface RefreshJob {
  id: string
  providers: string[]
  refresh_models: boolean
  status: 'queued' | 'running' | 'done' | 'failed'
  /** "cron", an account name, "token" or "loopback". */
  started_by: string
  started_at: number
  finished_at: number | null
  current: string | null
  elapsed_seconds: number
  log: string[]
  results: Record<string, unknown>
  error: string | null
}

/** The Data page: what is held, counted (GET /api/data/summary) */
export interface DataSummary {
  prices: {
    total: number; regions: number; stale: boolean
    clouds: { provider: Provider; records: number; regions: number; age_hours: number; collected_at: number; stale: boolean
      pinned: boolean; /** against the collection before; null at a cloud's first */ delta: number | null }[]
    kinds: { kind: string; label: string; records: number }[]
    history: { records: number; collections: number; since: number | null }
    moves_7_days: { cuts: number; rises: number }
  }
  news: {
    stale: boolean; lifecycle_pages: number; feeds: number
    changes: { total: number; last_24_hours: number; since: number | null; groups: { kind: string; label: string; items: number }[] }
    retirements: { total: number; firm: number; within_90_days: number; shown: boolean }
    announcements: { total: number; shown: boolean }
    sources: { source: string; label: string; retirements: number; firm: number; announcements: number; lifecycle: boolean; feed: boolean
      read_at: number | null; failed: string[]; stale: boolean }[]
  }
  models: { priced: number; sizing: number; page: string | null } | null
  refresh: { auto: boolean; busy: boolean; schedule: string; refresh_interval_hours: number | null; next_refresh_at: number | null
    last_run: { status: string; started_at: number | null; elapsed_seconds: number | null; finished: boolean; clouds: Record<string, string> } | null }
}

export interface AdminStatus {
  freshness: Freshness['providers']
  snapshots: Snapshot[]
  models: Record<string, number>
  busy: boolean
  jobs: RefreshJob[]
  /** How often the data is refreshed, in words: "every 3 hours". */
  refresh_schedule: string
  snapshot_total: number
}

export interface SnapshotPage {
  results: Snapshot[]
  total: number
  page: number
  per_page: number
  pages: number
}

export interface Whoami {
  admin: boolean; user: string | null; mfa?: boolean; /** the person's full name, where the account has one */ name?: string
  /** the password was right; the account's second step is wanted */ mfa_required?: boolean
  /** signed in, but two-step sign-in has to be set up before the admin opens */ setup_required?: boolean
  /** signed in, but a new password has to be chosen before the admin opens */ password_change_required?: boolean
  /** why, when it is the password itself that is no longer taken (too short, guessable, found in a breach) */ password_weak?: string
}
/** Two-step sign-in for the signed-in account */
export interface MfaState { user: string | null; enabled: boolean; pending: boolean; recovery_left: number; recovery_codes?: string[]
  /** every account on this site must have it */ required?: boolean }
export interface MfaSetup { secret: string; uri: string; qr_svg: string }


// --- OCI Generative AI -------------------------------------------------------
// OCI publishes which models it offers separately from what it charges for
// them, so the catalogue is its own thing: the price list alone would offer
// models Oracle has retired and could not say which run on a dedicated cluster.

export type OciMode = 'on_demand' | 'dedicated'

export interface OciGenAiModel {
  oci_name: string
  label: string
  vendor: string
  category: 'chat' | 'embed' | 'rerank' | 'voice'
  doc_url: string
  deprecated: boolean
  params_b: number | null
  context_tokens: number | null
  /** The name Oracle's pricing page uses, e.g. "Large Meta". */
  pricing_product: string | null
  on_demand: boolean
  dedicated: boolean
  modes: OciMode[]
  on_demand_regions: string[]
  dedicated_regions: string[]
  dedicated_shapes: { shape: string; raw_shape?: string; regions: string[] }[]
}

export interface OciUnitShape {
  shape: string
  gpu_model: string
  gpu_count: number
  gpu_mem_gb: number
  /** Multiplier in Oracle's price formula, not a count of GPUs. */
  ai_units: number
  service_limit: string
  total_vram_gb: number
}

export interface OciClusterFit extends OciUnitShape {
  regions: string[]
  /** Absent on import-route shapes: Oracle states the minimum, no VRAM estimate. */
  headroom_gb?: number
  /** null when the price list has no unambiguous AI-unit meter for the vendor. */
  usd_per_hour: number | null
  usd_per_month_24x7: number | null
}

export interface OciAiUnitRate {
  sku: string
  name: string
  usd_per_ai_unit_hour: number
  raw_unit: string
}

export interface OciDedicatedFit {
  oci_name: string
  label: string
  vendor: string
  modes: OciMode[]
  replicas: number
  params_billions: number | null
  doc_url: string
  precision?: string
  required_vram_gb?: number
  fits: OciClusterFit[]
  total_fitting: number
  /** "model" when Oracle publishes a shape table for this model, else "region". */
  shapes_source?: 'model' | 'region'
  rate?: OciAiUnitRate | null
  formula?: string
  largest_available_vram_gb?: number
  shortfall_gb?: number
  /** Why there is nothing to show, when there is nothing to show. */
  reason?: string
}


// --- hosting modes for one vendor -------------------------------------------

export type HostingMode = 'on_demand' | 'self_hosted' | 'dedicated'

/** One dedicated AI cluster unit shape Oracle will run the model on, priced. */
export interface DacCandidate {
  /** "import:H100_X2" -- pass back to choose this shape */
  key: string
  shape: string
  /** "import": Oracle's imported-models page; "pretrained": Oracle's own build */
  route: 'import' | 'pretrained'
  /** true for a shape Oracle names as the minimum for this model */
  minimum: boolean
  source: 'oracle-import-page' | 'model' | 'region'
  gpu_model: string
  gpu_count: number
  total_vram_gb: number
  ai_units: number
  usd_per_ai_unit_hour: number | null
  meter: string | null
  usd_per_hour: number | null
  usd_per_month_24x7: number | null
  regions: string[]
}

/** One self-hosted instance type the model fits on, at its cheapest region. */
/** The throughput used for one instance and its provenance. */
export interface Throughput {
  basis: 'benchmark' | 'oracle' | 'estimate' | 'assumption' | 'override'
  tokens_per_second: number
  note?: string
  /** estimate only */
  reference?: string
  steps?: string[]
  /** override only: what the data said */
  published?: Throughput
  /** oracle only */
  unit?: string
  scenario?: string
  concurrency?: number
  region_clause?: string
  /** benchmark only */
  match?: 'exact' | 'similar'
  benchmark_model?: string
  benchmark_precision?: string
  gpu?: string
  tp?: number
  replicas?: number
  idle_gpus?: number
  isl?: number
  osl?: number
  per_group_tokens_per_second?: number
  source?: string
  page_updated?: string
  version?: string
}

export interface GpuCandidate {
  /** the instance type; pass back to choose it */
  key: string
  instance: string
  region: string
  gpu: string
  gpu_model: string
  gpu_count: number
  total_vram_gb: number
  interconnect?: 'nvlink' | 'infinity-fabric' | 'pcie'
  required_vram_gb: number | null
  headroom_gb: number | null
  usd_per_hour: number
  usd_per_month_24x7: number
  gpu_fraction?: string
  note?: string | null
  /** NVIDIA's published figure for this model on this instance, if any */
  benchmark_tokens_per_second?: number | null
  /** a cluster, where no one server holds the model: how many servers, and what joins them */
  servers?: number
  server_vram_gb?: number
  cluster_network?: string
  cluster_source?: string
  usd_per_hour_per_server?: number
}

/** Data transfer out to the internet, per GB, in monthly tiers (cumulative GB). */
export interface EgressTier { from_gb: number; to_gb: number | null; usd_per_gb: number }
export interface EgressRates {
  provider: Provider
  available: boolean
  reason?: string
  region?: string | null
  price_region?: string
  tiers?: EgressTier[]
  free_gb?: number
  first_paid_usd_per_gb?: number | null
  lowest_usd_per_gb?: number | null
  free_scope?: string | null
  routing?: string
  internet_routing_usd_per_gb?: number
  first_paid_range_usd_per_gb?: [number, number]
  regions_priced?: number
  source?: string
}
/** The egress line of a hosting row: applied to self-hosting, else why not. */
export interface EgressLine {
  applied: boolean
  reason?: string
  mode?: 'text' | 'streamed' | 'same_region'
  /** bytes leaving per output token, the request envelope's share included */
  bytes_per_token?: number
  /** the pieces of the formula: bytes per output token, requests, their size */
  bytes_per_output_token?: number
  input_output_ratio?: number
  requests_per_month?: number
  prompt_tokens_per_request?: number
  envelope_bytes?: number
  extra_gb_per_month?: number
  gb_per_month?: number
  usd_per_month?: number
  region?: string | null
  price_region?: string
  free_gb?: number
  tiers?: EgressTier[]
  source?: string
  note?: string | null
}

export interface HostingRow {
  provider: Provider
  mode: HostingMode
  available: boolean
  reason?: string
  region_choice?: string
  region_label?: string
  monthly_usd?: number
  /** before egress, when egress was added */
  compute_monthly_usd?: number
  detail?: {
    egress?: EgressLine
    /** per-token */
    usd_per_1m_input?: number
    usd_per_1m_output?: number
    service_tier?: string | null
    meter?: string
    /** held to a place, a Global or Data Zone deployment: billed there, processed elsewhere */
    residency_note?: string | null
    /** per token: a note on the price itself (a sale price, with the regular one) */
    price_note?: string | null
    /** hourly modes: the instance or unit shape, bought `instances` times */
    instance?: string
    region?: string | null
    regions?: string[]
    gpu?: string
    total_vram_gb?: number
    required_vram_gb?: number
    usd_per_hour?: number
    instances?: number
    monthly_output_tokens_capacity?: number
    capacity_per_instance_per_month?: number
    /** where the instance's throughput came from: a published figure, or the assumption */
    throughput?: Throughput
    effective_usd_per_1m_output_tokens?: number
    ai_units?: number
    rate?: Partial<OciAiUnitRate> | null
    oci_name?: string
    /** "pretrained": Oracle's build of the model; "import": your weights, hosted. */
    via?: 'pretrained' | 'import'
    shapes_source?: 'model' | 'region' | 'oracle-import-page'
    /** dedicated: the priced shape's key, the suggested one, and every alternative */
    key?: string
    default_key?: string
    /** the suggested option as it reads ("4 × name" for a cluster) */
    default_instance?: string
    minimum?: boolean
    basis?: string
    alternatives?: DacCandidate[]
    /** self-hosted: every instance type that fits, cheapest first */
    instance_alternatives?: GpuCandidate[]
    /** self-hosted on several servers (`instance` then reads "4 × name"): because no one server holds the
     *  model (`cluster_needed`), or because the cluster is cheaper than, or picked over, a single server that does */
    servers?: number
    cluster_needed?: boolean
    server_vram_gb?: number
    cluster_network?: string
    cluster_source?: string
    usd_per_hour_per_server?: number
    /** set when a requested shape could not be used */
    shape_note?: string
  }
  assumptions?: Record<string, number | string>
  sensitivity?: SelfHostedCase[]
  breakeven?: BreakevenEstimate
}

/** Every vendor's hosting modes for one model in one answer: the Calculator's option grid. */
export interface HostingGrid {
  canonical: string
  vendors: Partial<Record<Provider, { results: HostingRow[]; cheapest: { mode: HostingMode; monthly_usd: number } | null }>>
  /** a vendor that could not be priced, with why; the others are unaffected */
  errors: Partial<Record<Provider, string>>
}

export interface VendorHosting {
  canonical: string
  provider: Provider
  label: string
  open_weights: boolean
  params_billions: number | null
  precision: string
  workload: {
    output_tokens_per_day: number
    input_output_ratio: number
    days: number
    output_tokens_per_month: number
  }
  results: HostingRow[]
  cheapest: { mode: HostingMode; monthly_usd: number } | null
}


/** An open-weight model Oracle lists as importable to a dedicated AI cluster. */
export interface OciImportedModel {
  hf_id: string
  vendor: string
  capability: string
  /** Oracle's minimum unit shape per accelerator class. */
  min_shapes: string[]
  doc_url: string
}

export interface OciImportFit {
  hf_id: string
  vendor: string
  capability: string
  doc_url: string
  replicas: number
  params_billions: number | null
  fits: OciClusterFit[]
  total_fitting: number
  rate: OciAiUnitRate | null
  shapes_source: 'oracle-import-page'
  formula: string
  note: string
  reason?: string
  /** set when a minimum shape is named on Oracle's page but missing from its unit-shape table:
   *  its AI units are then the hardware's per-unit count times the number after X */
  derived_note?: string
}

/** Everything OCI Generative AI offers for one catalogue model. */
export interface OciHosting {
  canonical: string
  pretrained: OciGenAiModel | null
  dedicated_fit: OciDedicatedFit | null
  import: OciImportedModel | null
  import_fit: OciImportFit | null
}


/** One thing that changed between two snapshots, in a sentence. */
export interface NewsItem {
  id: number
  happened_at: number
  provider: 'aws' | 'azure' | 'oci' | 'core42' | 'alibaba' | 'gcp' | 'nebius' | 'mistral' | 'telnyx' | 'snowflake' | 'cloudflare' | 'databricks' | 'fireworks' | 'deepinfra' | 'gemini' | 'hfendpoints' | 'watsonx' | 'xai' | 'together' | 'crusoe' | 'huawei' | 'groq' | 'huggingface' | 'oci-genai' | 'openai' | 'anthropic' | 'fireworks' | 'huggingface' | 'oci-genai' | 'openai' | 'anthropic'
  kind: string
  headline: string
  detail: Record<string, unknown>
  snapshot_id: number | null
  old_snapshot_id: number | null
  /** the change's own page, under the site's home, when it is notable enough to have one */
  page?: string
  /** how much the change matters, for ordering; 0 is not a story */
  score?: number
  /** a price move in money: the default workload before and after */
  /** a retirement of a model this app prices: its label */
  priced?: string
  impact?: { text: string; monthly_old?: number; monthly_new: number; rank_old?: number; rank_new?: number; of?: number }
  /** what the story is (launch, price, retire, region, feature, bulk, hf, site) and where it goes (pricing/stories.py) */
  cat?: StoryCat
  place?: 'lead' | 'show' | 'fold' | 'hide'
  /** items of several sources that are one event share a key; the story's title and its sources */
  story_key?: string
  story_title?: string
  story_sources?: NewsItem['provider'][]
}
export type StoryCat = 'launch' | 'price' | 'retire' | 'region' | 'feature' | 'bulk' | 'hf' | 'site'

/** a model with a firm retirement date in the next 90 days */
export interface Retiring {
  source: NewsItem['provider']; model: string; mode: string; retires_at: string
  replacement: string | null; url: string | null; days_left: number
  priced: { canonical: string; label: string } | null
}
/** one of the figures at the head of Trending: a count, and the kinds it filters the feed to */
export interface NewsFigure { key: string; label: string; n: number; kinds: string[]; exclude?: string[]; cats?: string[]; places?: string[] }
/** the head of Trending: the largest move as one sentence, the rest in words, the figures */
export interface NewsLede { headline: string; dek: string; figures: NewsFigure[]; period: string }
export interface NewsFeed {
  results: NewsItem[]; summary: string[]; days: number | null
  lede?: NewsLede
  what_it_means?: boolean; retiring?: Retiring[]
}
export interface PriceHistory {
  provider: string; name: string; unit: string | null
  points: { at: number; price: number; snapshot_id: number }[]
  snapshots?: number; first_at?: number
}


export interface SystemStatus {
  time: number
  host: { platform: string; python: string; cpus: number | null; arch: string }
  cpu: { percent: number | null; loadavg: number[] | null }
  memory: { total_mb: number; available_mb: number; used_mb: number; used_pct: number | null; swap_used_mb: number } | null
  disk: { path: string; total_gb: number; free_gb: number; used_pct: number } | null
  api: { rss_mb: number | null; uptime_seconds: number; pid: number }
  vm_uptime_seconds: number | null
  database: { path: string; size_mb: number }
  checks: { name: string; ok: boolean; detail: string }[]
}


/** Two or three (cloud, mode) options side by side, with where they cross. */
export interface OptionsComparison {
  canonical: string
  label: string
  precision: string
  regions: RegionChoices
  workload: {
    output_tokens_per_day: number; input_output_ratio: number; days: number
    output_tokens_per_month: number; input_tokens_per_month: number
  }
  assumptions: { tokens_per_second: number; utilization: number; capacity_per_instance_per_month: number; hours_per_month: number }
  /** the egress assumption, when egress was asked for */
  egress?: { mode: string; bytes_per_token: number; extra_gb_per_month: number; gb_per_month: number } | null
  benchmarks?: { source: string | null; page_updated: string | null; trtllm_version: string | null; oracle?: { source: string | null } }
  options: (HostingRow & { label: string })[]
  at_workload: { monthly_usd: (number | null)[]; winner: number | null; margin_usd: number | null }
  axis: { from: number; to: number; points: number[] }
  curves: (number | null)[][]
  segments: { from: number; to: number; winner: number | null }[]
  breakevens: { output_tokens_per_month: number; from: number; to: number; monthly_usd: number | null }[]
  /** the same options month by month, when a period was asked for */
  over_time?: OverTime
}

/** Options over a period as the volume grows (tools.cost_over_time): today's list prices throughout. */
export interface OverTime {
  months: number
  growth_pct: number
  growth_given_as: 'rate' | 'end volume'
  output_tokens_per_day: { first: number; last: number }
  /** the volume each month, first to last */
  output_tokens_per_day_by_month?: number[]
  output_tokens_in_period: number
  options: {
    label: string; provider: Provider; mode: string; available: boolean; reason?: string | null
    monthly_usd?: number[]; total_usd?: number; first_month_usd?: number; last_month_usd?: number
    /** whole instances or cluster units per month; null for a per-token option */
    units?: number[] | null
    step_ups?: { month: number; units: number }[]
  }[]
  cheapest: number | null
  margin_usd: number | null
  cheapest_by_month: (number | null)[]
  switches: { month: number; from: number | null; to: number | null }[]
  basis: string
}


// --- administrator settings --------------------------------------------------

export type SuggestionRule = 'lowest' | 'highest'

export interface CalculatorDefaults {
  model: string
  /** '' = the weights' native precision */
  precision: string
  output_tokens_per_day: number
  input_tokens_per_day: number
  tokens_per_second: number
  utilization: number
  /** "provider:mode", two or three */
  options: string[]
}

/** What every page needs from the administrator's settings. */
export interface UiSettings {
  vendors: Provider[]
  /** per vendor, the hosting modes the administrator shows */
  modes: Record<Provider, HostingMode[]>
  suggestion_rule: SuggestionRule
  calculator_defaults: CalculatorDefaults
  notice: string
  /** the site is closed to visitors: only an administrator gets the app then, and it says so */
  maintenance?: boolean
  /** the administrator's switch for the OCI position banner */
  /** the Time step and Cost over time are shown */
  cost_over_time?: boolean
  /** the Calculator's Reserved capacity panel is shown */
  reserved_capacity?: boolean
  /** the assessment: offered, free or at what price (api/payments.py); the price is told only when it is charged */
  assessment?: { enabled: boolean; free?: boolean; price_usd: number | null; currency: string }
  /** Trending: "What it means" under a price move */
  trending?: { what_it_means: boolean; retirements?: boolean; announcements?: boolean }
  /** the newsletter sign-up in the footer */
  newsletter?: boolean
  /** the pages made from the data: the links to them for the footer, and which kinds a page may link to */
  explore?: { links: { path: string; label: string }[]; live: string[] }
  /** each view's address under the site's home, its title and its description (api/seo.py) */
  pages?: Record<string, { path: string; title: string; description: string }>
  /** the Umami website on this host, once set up */
  analytics?: { umami_website_id: string | null }
  /** the email field on Export PDF */
  export_email?: 'optional' | 'required' | 'off'
  /** who runs the site, for the footer and the legal pages */
  site?: SiteDetails
  /** which site this server is: the name, the header layout, what is on */
  profile?: SiteProfile
}

export interface UsageRow {
  backend: string; model: string; calls: number; input_tokens: number; output_tokens: number; sessions: number
  usd: number | null; rate_in: number | null; rate_out: number | null; rate_source: string
}
export interface UsagePeriod { by_model: UsageRow[]; calls: number; input_tokens: number; output_tokens: number; sessions: number; usd: number; priced: boolean }
export interface AdminActivity {
  now: number
  usage: Record<'today' | 'last_7_days' | 'last_30_days' | 'all_time', UsagePeriod>
  daily: { day: string; calls: number; input_tokens: number; output_tokens: number; sessions: number }[]
  session_token_budget: number
}

export interface AdminSettingsDoc {
  /** Admin > Data checks: the last hand check of a vendor's prices, per vendor id; null in a patch removes one */
  verified?: Record<string, { checked_on: string; by?: string; note?: string } | null>
  vendors: Record<Provider, boolean>
  modes: Record<Provider, Partial<Record<HostingMode, boolean>>>
  ask: { session_token_budget: number; daily_token_budget: number }
  disabled_models: string[]
  suggestion_rule: SuggestionRule
  calculator_defaults: CalculatorDefaults
  notice: string
  /** the Time step and Cost over time are shown */
  cost_over_time?: boolean
  /** the Calculator's Reserved capacity panel is shown */
  reserved_capacity?: boolean
  /** the assessment: on; free, or the price in whole US dollars (kept while it is free) */
  assessment?: { enabled: boolean; price_usd: number; free?: boolean; teams_link?: string }
  trending?: { what_it_means: boolean; retirements?: boolean; announcements?: boolean }
  /** the newsletter sign-up in the footer */
  newsletter?: boolean
  analytics?: { enabled: boolean; umami_website_id: string }
  export_email?: 'optional' | 'required' | 'off'
  request_log?: boolean
  monitor: { enabled: boolean; refresh_seconds: number }
  /** Admin > Automatic pages: the pages made from the data, their switches */
  auto_pages: {
    kinds: Record<string, boolean>; lists: Record<string, { on: boolean; footer: boolean }>; notable: Record<string, boolean>
    min_price_move_pct: number; report_in_progress: boolean; sitemap_sizing: number; countries: string[]; hidden: string[]
  }
  /** Admin > Search engines: the listing switch, the site's address, each page's own title and description */
  accounts: { registration: boolean }
  seo: { listed: boolean | null; public_url: string; pages: Record<string, { title?: string; description?: string; sitemap?: boolean }>; indexnow: boolean; google: boolean; bing: boolean }
  /** who runs the site, for the footer and the legal pages */
  site?: SiteDetails
  refresh?: { auto: boolean }
  /** the catalogue check's mail: null leaves the server's default (on in production only) */
  catalogue_check?: { email: boolean | null }
  retention?: { keep: number; unit: RetentionUnit }
}

export interface AuditEntry { at: number; user: string; field: string; old: unknown; new: unknown }

export interface AdminModel {
  canonical: string
  label: string
  family: string
  providers: Provider[]
  open_weights: boolean
  params_billions: number | null
  enabled: boolean
}

export interface AdminSettings {
  settings: AdminSettingsDoc
  vendors: { provider: Provider; label: string; enabled: boolean; models: number }[]
  models: AdminModel[]
  audit: AuditEntry[]
}

export interface AdminUser { username: string; created_at: number; mfa: number; name: string; phone: string
  status: 'active' | 'pending' | 'locked'; /** chooses a new password at the next sign-in */ must_change_password: number
  /** this account must have two-step sign-in: without it, its sign-in is the setup */ mfa_required: number
  /** 0: a request whose address has not been confirmed yet (the link sent to it was not opened) */ email_verified: number }

export interface VendorSnapshots {
  provider: Provider
  label: string
  current_id: number | null
  latest_id: number | null
  pin: { provider: string; snapshot_id: number; pinned_at: number; pinned_by: string } | null
  live_rows: number
  archive_rows: number
  archived: number
  snapshots: { id: number; created_at: number; record_count: number; table: 'live' | 'archive' }[]
}

/** /models/{canonical}/spec: what the precision picker is built from. */
export interface ModelSpec {
  canonical: string
  label: string
  params_billions: number | null
  /** fp32 | fp16 | bf16 | fp8 | int8 | int4, or null when unknown */
  native_precision: string | null
  /** what Hugging Face reports: BF16, F8_E4M3, MXFP4, NVFP4 ... */
  native_precision_raw?: string | null
  /** GB of weight files the repo publishes; set only where the precision is not known */
  published_weights_gb?: number | null
  active_params_billions?: number | null
  license?: string | null
  open_weights?: boolean
  arch?: ModelArch | null
  source: 'huggingface' | 'seed'
}

/** Admin > Hardware (pricing/hardware.py) */
export type Interconnect = 'nvlink' | 'infinity-fabric' | 'pcie'
export interface HardwareEvidence {
  role: 'provider' | 'gpu'
  url: string
  status: 'ok' | 'unreachable'
  counts?: Record<string, number>
  snippets?: { keyword: string; text: string }[]
  suggests?: Interconnect | null
}
export interface HardwareCandidate {
  provider: 'aws' | 'azure' | 'oci'
  name: string
  region: string
  gpu_model: string | null
  gpu_count: number | null
  gpu_memory_reported: string | null
  usd_per_hour: number | null
  usd_per_gpu_hour?: number
  part_number?: string
  family?: string
  series?: string
  feed: string
  /** why the shipped catalogue leaves this out on purpose, when it does */
  unmapped_note?: string | null
  evidence?: HardwareEvidence[]
}
export interface HardwareCheck {
  id: string
  providers: string[]
  started_by: string
  status: 'running' | 'done' | 'failed'
  started_at: number
  finished_at: number | null
  elapsed_seconds: number
  log: string[]
  error: string | null
  candidates: HardwareCandidate[] | null
  errors: Record<string, string> | null
}
export interface HardwareOverride {
  provider: 'aws' | 'azure' | 'oci'
  name: string
  gpu_model: string
  gpu_count: number
  gpu_mem_gb: number
  interconnect: Interconnect
  source_url: string
  match: string | null
  note: string | null
  added_by: string
  added_at: number
}
export interface HardwareState {
  overrides: HardwareOverride[]
  last_check: ({ checked_at: number; providers: string[]; candidates: HardwareCandidate[]; errors: Record<string, string>; by?: string }) | null
  running: HardwareCheck | null
  sources: {
    discovery: Record<string, { name: string; url: string; what: string }>
    evidence: { aws: string; azure: string; oci: string; nvidia: Record<string, string> }
  }
  interconnects: Interconnect[]
  known: Record<'aws' | 'azure' | 'oci', string[]>
}

export interface SiteDetails { operator: string; contact_email: string; jurisdiction: string
  /** the codes Search Console and Bing Webmaster Tools give to prove the site is yours (admin only) */
  google_verification?: string; bing_verification?: string }
export interface Subscriber { email: string; created_at: number; source: string | null }

export interface ContactMessage {
  id: number; created_at: number; name: string; email: string; topic: string; message: string
  forwarded: number; read_at: number | null
}
/** A simulation's document (api/simulations.py): what the page edits and what is kept under the link. */
export interface SimulationWorkload {
  label: string; canonical: string; output_tokens_per_day: number; input_output_ratio: number
  precision?: string | null
  options: { provider: Provider; mode: HostingMode; shape?: string | null }[]
  choice: number
  /** Optimise: what the workload is for, and the usual size of a request */
  use_case?: string | null; context_tokens?: number | null
}
export interface SimulationDoc {
  name: string; client: string; regions?: Record<string, string> | null
  months: number; growth_pct: number; end_output_tokens_per_day?: number | null
  workloads: SimulationWorkload[]; notes: string
}
/** A simulation priced (tools.simulate) */
export interface Simulated {
  months: number; growth_pct: number; regions: RegionChoices
  workloads: {
    index: number; label: string; canonical: string; model: string; precision: string; choice: number
    chosen: OverTime['options'][number]; cheapest: number | null; options: OverTime['options']
    output_tokens_per_day: { first: number; last: number }
  }[]
  totals: { monthly_usd: number[]; total_usd: number; first_month_usd: number; last_month_usd: number
    cheapest_mix_usd: number; saving_usd: number; unpriced: string[] }
  basis: string
}
export interface SimulationRecord { id: string; created_at: number; updated_at: number; doc: SimulationDoc; priced: Simulated }
/** Admin > Feedback (api/feedback.py) */
export interface AdminFeedback {
  thumbs: Record<string, { up: number; down: number; notes: { id: number; ts: number; up: boolean; note: string }[] }>
  thumbs_total: number
  questions: { id: number; ts: number; question: string; lookups: number; failed: number; ms: number }[]
  questions_failed: number
  purposes: Record<string, { label: string; n: number }>
  purpose_notes: { id: number; created_at: number; kind: string; purpose: string | null; note: string }[]
}
/** One Ask conversation as kept for review (api/feedback.py): the exchanges in order */
export interface AskConversation {
  id: string; started: number; last: number; failed: number
  turns: { id: number; ts: number; question: string; answer: string | null; lookups: number
           calls: { name: string; input?: unknown }[]; failed: boolean; error: string | null; ms: number }[]
}
export interface AdminAskLog { conversations: AskConversation[]; exchanges: number; failed: number; unanswered: number; kept_days: number }
/** An assessment request, paid or free (api/payments.py); "requested" is a free one waiting to be delivered */
export interface AssessmentOrder {
  id: number; created_at: number; name: string; email: string; company: string; brief: string; context: string
  amount_usd: number; currency: string; status: 'pending' | 'paid' | 'requested' | 'delivered' | 'failed'
  stripe_session_id: string | null; stripe_payment_intent: string | null; amount_paid: number | null
  paid_at: number | null; delivered_at: number | null; notified: number
  /** the call before the assessment (api/calls.py): the times offered (epoch seconds), its length, the meeting
   *  link, the time chosen, what the person wrote if none suited, where it stands, and the person's link */
  call_slots: number[]; call_minutes: number | null; call_tz: string | null; call_link: string | null
  call_proposed_at: number | null; call_at: number | null; call_chosen_at: number | null; call_note: string | null
  call_state: 'none' | 'offered' | 'declined' | 'set'; call_url: string
}
/** What the page behind the emailed link is told about a call: a name, the times, where it stands */
export interface CallInfo {
  name: string; email: string; minutes: number; state: 'offered' | 'declined' | 'set'
  slots: { at: number; past: boolean }[]; chosen: number | null; link: string | null; site: string
}
export interface IssueRequest { subject: string; intro: string; days: number; sections: string[]; site_url: string }
export interface IssueDraft { subject: string; html: string; text: string; sections: string[] }
export interface NewsletterIssue {
  id: number; created_at: number; subject: string; recipients: number; sent: number; failed: number
  status: 'sending' | 'sent' | 'failed'; sent_by: string | null; error: string | null
}
export interface NewsletterStatus {
  mail: { configured: boolean; host: string; port: number; sender: string; security: string; authenticated: boolean }
  subscribers: number
  issues: NewsletterIssue[]
  sections: { id: string; label: string }[]
  default_subject: string
}

/** What the site is (pricing/site.py): LLM Opex. */
export interface SiteProfile {
  id: 'opex'
  name: string
  tagline: string
  /** where to write, unless Admin > Website > Site details says otherwise */
  contact_email?: string
  /** "wide": the name large on the left, menu and controls on one line; "classic": the older header */
  layout: 'wide' | 'classic'
  features: { landing: boolean; footer: boolean; newsletter: boolean; legal: boolean; question_titles: boolean; locate: boolean; explore?: boolean }
  /** the clouds Compare opens with; empty means all */
  compare_default: Provider[]
}

/** A report someone exported, with who asked for it (api/exports.py) */
export interface ExportedReport {
  /** what the report was for, from the Export form (api/feedback.PURPOSES) */
  purpose?: string | null; purpose_note?: string | null
  id: number; created_at: number; name: string; email: string; title: string; kind: string; model: string | null
  subscribed: number; storage: 'bucket' | 'disk' | null; filename: string | null; size_bytes: number | null
}
export interface ExportedReports {
  results: ExportedReport[]; count: number; total_bytes: number; people: number
  storage: { bucket: string | null; kept_in_bucket: number; kept_on_disk: number }
  asking: boolean; required?: boolean; mode?: 'optional' | 'required' | 'off'
  /** kept with both a name and an email */
  named?: number
  /** every export the server counted, kept or not */
  downloads?: { total: number; since: string | null; by_day: { day: string; kind: string; n: number }[] }
}

/** Admin > Traffic: visitor statistics read from Umami (api/traffic.py) */
export interface Traffic {
  days: number; enabled: boolean; configured: boolean; reachable: boolean; error?: string
  /** visitors in the last five minutes */
  active?: number | null
  /** page views the server itself counted over the same days, which no blocker hides */
  counted: { page_views: number; since: string }
  stats?: { pageviews: number; visitors: number; visits: number; bounces: number; totaltime: number }
  series?: { pageviews: { x: string; y: number }[]; sessions: { x: string; y: number }[] }
  top?: Record<string, { x: string; y: number }[]>
  labels?: Record<string, string>
}

/** Admin > Traffic, click streams (api/traffic.py): each answer carries Umami's state around its data */
export interface UmamiRead { configured: boolean; reachable: boolean; error?: string; days?: number }
export interface TrafficVisitRow {
  id: string; first: number; last: number; seconds: number; referrer: string; source: string | null
  country: string; device: string; os: string; browser: string; pages: string[]; actions: string[]; did: string[]; views: number
}
export interface TrafficVisits extends UmamiRead { total?: number; visits?: TrafficVisitRow[]; filters?: string[]; crawlers_left_out?: number }
export interface CrawlerSignature {
  country: string; os: string; browser: string; device: string; visits: number; views: number; pages: number
  burst_visits: number; steady_visits: number
  top_pages: { name: string; visits: number }[]; first: number; last: number; bursts: { at: number; visits: number }[]
}
export interface TrafficCrawlers extends UmamiRead {
  undeclared?: { signatures: CrawlerSignature[]; visits: number; all_visits: number; share: number | null; burst_min: number; burst_seconds: number }
  declared: { days: number; bots: { name: string; kind: string; kind_label: string; last_seen: number; hits: number; pages: number }[] }
}
export interface TrafficStep { at: number; kind: 'page' | 'event'; name: string; referrer: string | null; data: Record<string, string | number> | null }
export interface TrafficVisit extends UmamiRead { id?: string; steps?: TrafficStep[]; first?: number | null; last?: number | null; seconds?: number }
export interface TrafficPaths extends UmamiRead {
  from?: string; visits?: number
  next?: { page: string | null; visits: number; share: number; then: { page: string | null; visits: number }[] }[]
}
export interface TrafficFunnels extends UmamiRead {
  funnels?: { name: string; window_minutes: number; steps: { type: string; value: string; visitors: number; dropped: number }[] }[]
}
export interface TrafficRetention extends UmamiRead {
  cohorts?: { date: string; visitors: number; back_7: number; back_30: number; share_7: number | null; share_30: number | null }[]
}
export interface TrafficUtm extends UmamiRead { utm_source?: UtmRow[]; utm_medium?: UtmRow[]; utm_campaign?: UtmRow[]; utm_content?: UtmRow[] }
export interface UtmRow { name: string; visits: number }
export interface OutcomeSide {
  visits: number; pages_mean?: number; seconds_median?: number
  pages?: ShareRow[]; first_pages?: ShareRow[]; sources?: ShareRow[]; actions?: ShareRow[]
}
export interface ShareRow { name: string; visits: number; share: number }
export interface TrafficOutcomes extends UmamiRead { event?: string; label?: string; did?: OutcomeSide; rest?: OutcomeSide; share?: number | null }
export interface TrafficCompare extends UmamiRead {
  lists?: { event: string; property: string; label: string; values: { value: string; count: number }[] }[]
}

/** Admin > Overview (api/overview.py) */
/** One thing a refresh read on a vendor's pages and could not use, or had to work out. */
export interface CatalogueFinding {
  key: string; grp: 'attention' | 'handled'; kind: string; title: string; detail: string; url: string | null
  first_seen: number; last_seen: number; cleared_at: number | null; notified_at: number | null
}
/** GET /admin/catalogue-check */
export interface CatalogueCheck {
  attention: CatalogueFinding[]; handled: CatalogueFinding[]; cleared: CatalogueFinding[]
  checked_at: number | null
  /** whether a new item that needs a look is mailed: the administrator's choice, else on in production only */
  email: boolean; mail_configured: boolean; contact: string; cleared_days: number
}

/** GET /admin/data-checks/assumed?provider= */
export interface AssumedPrices {
  provider: Provider; total: number
  groups: { assumption: string; count: number; source: string | null
    rows: { name: string; unit: string; price_usd: number; pricing_model: string; service_tier: string | null; region: string; raw: string | null }[] }[]
}

/** GET /admin/data-checks */
export interface DataChecks {
  checked_at: number | null
  crosscheck: {
    summary: CatalogueFinding | null; mismatches: CatalogueFinding[]; unread: CatalogueFinding[]
    /** the two feeds compared against, as configured */
    feeds: { name: string; label: string; url: string; page: string }[]
    /** the makers whose prices are compared */
    makers: Record<string, string>
    /** what the last run compared against: per feed, read or not, how many models it listed and matched ours, when */
    last_run: { at: number; ours: number; compared: number; agree: number; makers: string[]
      sources: { name: string; label: string; url: string; page: string; read: boolean; models: number | null; matched: number; agree: number; read_at: number | null }[] } | null
  }
  suite: { kind: string; label: string; grp: 'attention' | 'handled'; items: CatalogueFinding[] }[]
  vendors: { provider: Provider; label: string; rows: number; assumed: number; collected_at: number | null; sources: string[]
    verified: { checked_on: string; by?: string; note?: string } | null; findings: number }[]
  cleared: CatalogueFinding[]
}

export interface AdminOverviewData {
  now: number; site: string
  /** Admin > Data checks: findings that need a look from the cross-check and the correctness suite */
  data_checks?: { attention: number }
  has: { reports: boolean; newsletter: boolean; messages: boolean; assessments?: boolean }
  visitors: { configured: boolean; reachable: boolean; page_views_counted: number; visitors?: number; page_views?: number; active?: number }
  reports: { total: number; today: number; last_7_days: number; asking: boolean; bucket: string | null; on_disk: number
    recent: { id: number; created_at: number; name: string; title: string; kind: string }[] }
  subscribers: { total: number; today: number; last_7_days: number; mail_configured: boolean }
  accounts?: { total: number; without_two_step: number; waiting?: number; locked?: number; unnamed?: number; /** required of them and not set up yet */ to_set_up?: number
    /** requests whose address is not confirmed yet */ unconfirmed?: number }
  seo?: { listed: boolean; problems: number | null }   // null until the server's first page audit is done
  messages: { total: number; unread: number }
  assessments?: { total: number; open: number; paid_usd: number }
  data: { providers: number; stale: string[]; newest_age_hours: number | null; oldest_age_hours: number | null; prices: number; auto_refresh: boolean }
  /** the catalogue check: how many findings leave something missing or unpriced on the site */
  catalogue?: { attention: number }
  server: { checks: number; failing: string[]; memory_used_pct: number | null; disk_used_pct: number | null }
  llm: { calls: number; tokens: number; conversations: number }
  cloud_cost: { total: number; currency: string | null; month: string } | null
  /** the site is closed to visitors (Admin > Maintenance) */
  maintenance?: { on: boolean }
  /** the nightly copy of the database (api/backup.py) */
  backup?: { where: 'bucket' | 'disk'; age_hours: number | null; stale: boolean; failed: boolean
    last: { at: number; ok: boolean; where: string | null; object: string | null; bytes: number; removed: number; error?: string } | null }
}

/** Admin > Server: what Glances reports, shaped by api/monitor.py */
export interface ServerLive {
  configured: boolean; reachable: boolean; time: number; error?: string
  /** the administrator's switch, how often the page should ask, and the container's state while it is off */
  enabled?: boolean; refresh_seconds?: number; container?: string | null
  system?: string | null; uptime?: string | null
  cpu?: { total: number | null; user: number | null; system: number | null; iowait: number | null; steal: number | null; cores: number | null; per_core: (number | null)[] }
  load?: { min1: number | null; min5: number | null; min15: number | null }
  memory?: { total: number | null; used: number | null; available: number | null; percent: number | null }
  swap?: { total: number | null; used: number | null; percent: number | null }
  disks?: { device: string; size: number; used: number; percent: number | null }[]
  disk_io?: { read_bps: number; write_bps: number }
  site_traffic?: { rx_bps: number | null; tx_bps: number | null } | null
  containers?: { name: string; status: string; uptime: string | null; cpu_percent: number; memory: number | null; memory_limit: number | null; rx_bps: number | null; tx_bps: number | null }[]
  processes?: { total: number | null; running: number | null; top: { pid: number; name: string; user: string | null; cpu_percent: number; memory_percent: number; rss: number | null; command: string }[] }
}
export interface ServerSample { ts: number; cpu: number | null; mem: number | null; load1: number | null; disk: number | null; rx_bps: number | null; tx_bps: number | null }
export interface ServerHistory { hours: number; every_seconds: number; points: ServerSample[]; kept?: { samples: number; oldest: number | null }; keep_days?: number }
/** Admin > Server: the proxy's access log, counted (api/requestlog.py) */
export interface RequestLine { ts: number; method: string; path: string; status: number; ms: number; size: number }
export interface RequestLog {
  enabled: boolean; available: boolean; minutes: number
  per_minute?: { t: number; n: number; errors: number }[]
  totals?: { requests: number; ok: number; client_errors: number; server_errors: number; ms_median: number | null; ms_p95: number | null; bytes: number }
  top?: { path: string; n: number; ms_avg: number; ms_max: number; errors: number }[]
  slowest?: RequestLine[]
  problems?: RequestLine[]
}
/** Admin > Usage and cost: the cloud bill so far this month (api/cloud_cost.py) */
export interface CloudCost {
  configured: boolean; ok: boolean; error?: string
  currency?: string | null; month?: string; total?: number; last_month_total?: number; projected?: number | null
  days_so_far?: number; days_in_month?: number; fetched_at?: number
  by_service?: { service: string; cost: number }[]
  daily?: { day: string; cost: number }[]
  /** OCI bills in another currency: what was billed, and the ECB rate the dollars were worked out at */
  billed?: { currency: string; total: number; last_month_total: number }
  rate?: { source: string; currency: string; usd: number; day: string }
  /** no rate could be read: the figures are as billed, and this says why */
  rate_error?: string
}

/** Admin > Models: one model of the whole list, compared across clouds or sized only (api/admin_models.py) */
export interface AdminCatalogModel {
  canonical: string; label: string; family: string; params_b: number | null
  kind: 'compared' | 'sizing'; providers: Provider[]; license: string | null; open_weights: boolean
  downloads: number | null; enabled: boolean
  status: { state: 'live' | 'retiring' | 'retired' | 'confirm'; date: string | null; providers: Provider[]; note: string }
}
export interface AdminModelsData {
  models: AdminCatalogModel[]; default_model: string; disabled: string[]
  retirements: {
    auto_hide: boolean; within_90_days: number
    to_confirm: { key: string; keys: string[]; modes: string[]; source: Provider; model: string; retires_at: string; replacement: string | null; canonical: string; label: string }[]
    hidden: { key: string; source: Provider; name: string; retires_at: string; shown_again: boolean; input_per_1m: number | null }[]
  }
}

/** Admin > Website > Trending (api/trending_admin.py) */
export type StoryPlace = 'lead' | 'show' | 'fold' | 'hide'
export interface TrendingAdmin {
  places: Record<StoryCat, StoryPlace>; default_places: Record<StoryCat, StoryPlace>; muted: string[]; days: number
  sources: Record<string, number>
  stories: { key: string; title: string; cat: StoryCat; place: StoryPlace; sources: string[]; at: number; score: number; ids: number[] }[]
  hidden: { id: number; provider: string; headline: string; at: number }[]
  hidden_ids: number[]
}

/** Admin > Maintenance (api/maintenance.py) */
export interface MaintenanceState {
  enabled: boolean; message: string; until: string; reopen: boolean; since: string
  /** closed to visitors now (on, and not past a time it was told to open at) */
  active: boolean
  standard_message: string
  history: { at: number; user: string; on: boolean }[]
}

/** Admin > Search engines (api/seo_admin.py) */
export interface SeoPage {
  path: string; address: string | null; title: string; description: string; default_title: string; default_description: string
  custom: string[]; sitemap: boolean; static: boolean; listed: boolean
  /** a list page or a country page made from the data */
  auto?: boolean
  checks: { level: 'bad' | 'warn' | 'info'; text: string }[]
}
export interface SeoState {
  site: string
  listing: { listed: boolean; setting: boolean | null; default: boolean; locked: string | null }
  address: { effective: string | null; setting: string; from_server: string | null }
  pages: SeoPage[]; problems: number; sitemap_pages: number
  /** the data pages' titles: when last read, whether a reading is under way, how many are kept */
  audit: { at: number | null; running: boolean; pages: number }
  /** the pages made from the data: how many of each kind, and how many the sitemap lists */
  data_pages?: { priced_models?: number; sizing_models?: number; clouds?: number; gpus?: number; countries?: number; in_sitemap?: number }
  limits: { title: number; description: number }
  search: { configured: boolean; reachable: boolean; days?: number; search?: number; ai?: number
    previous?: { search: number; ai: number }; sources?: { name: string; kind: 'search' | 'ai'; visits: number }[]
    daily?: { day: string; visits: number }[]; landing?: { path: string; visits: number }[] }
  crawlers: { days: number; bots: { name: string; kind: string; kind_label: string; last_seen: number; hits: number; pages: number }[]
    sitemap_fetched: { at: number; by: string } | null; robots_fetched: { at: number; by: string } | null }
  verification: { google: string; bing: string }
  links: { robots: string; sitemap: string; image: string } | null
  indexnow: IndexNowState
  /** the administrator's switch for each engine's own tool */
  engines: { google: boolean; bing: boolean }
}
/** Telling Bing and the other IndexNow engines about new and changed pages (api/indexnow.py) */
export interface IndexNowState {
  on: boolean; active: boolean; why_not: string | null; announced: number; waiting?: number; key_file: string | null
  last: { at: number; count: number; ok: boolean; detail: string } | null
}
/** What Google's or Bing's own tool says of the site (api/searchconsole.py) */
export interface ConsoleTotals { clicks: number; impressions: number; ctr: number; position: number | null }
export interface ConsoleRow { name: string; clicks: number; impressions: number; position: number }
export interface ConsoleState {
  engine: 'google' | 'bing'; configured: boolean; ok: boolean; days: number; error?: string; off?: boolean
  site?: string; from?: string; to?: string; note?: string; checked_at?: number
  totals?: ConsoleTotals; previous?: ConsoleTotals
  daily?: { day: string; clicks: number; impressions: number }[]; queries?: ConsoleRow[]; pages?: ConsoleRow[]
}
export interface SeoConnection { on: boolean; configured: boolean; ok: boolean; site: string | null; error: string | null; account?: string | null; key_file?: string }
export interface SeoSitemap { path: string; last_read: string | null; submitted_at: string | null; addresses: number; pending?: boolean; errors?: number; warnings?: number; status?: string }
export interface SeoIndexState {
  google: { configured: boolean; error: string | null; sitemaps: SeoSitemap[]; total: number; unchecked: number; checks: boolean
    counts: { indexed: number; waiting: number; not_kept: number; broken: number }; oldest_check: number | null
    pages: Record<'indexed' | 'not_kept' | 'broken', { path: string; coverage: string; last_crawl: string }[]> }
  bing: { configured: boolean; error: string | null; sitemaps: SeoSitemap[]; in_index: number | null; crawled?: number | null; errors?: number | null; as_of?: string | null }
  indexnow: IndexNowState; checking: boolean
  connections: { google: SeoConnection; bing: SeoConnection; checked_at?: number }
}
export interface SeoLive { known: boolean; checked_at?: number; checks: { what: string; url: string; ok: boolean; detail: string }[] }

/** The pages made from the data (api/catalog.py, /api/pages/<path>) */
interface ExploreHead { title: string; description: string }
export interface ModelLink { slug: string; label: string }
export interface ModelPageData extends ExploreHead {
  kind: 'model'; slug: string; canonical: string; label: string; maker: string; priced: boolean; open_weights: boolean
  facts: { params_b: number | null; active_params_b: number | null; architecture: string | null; context: number | null
    license: string | null; precision: string | null; hf_id: string | null; downloads: number | null }
  tokens: { provider: string; cloud: string; input: number | null; output: number | null; region: string | null; regions: number; sold: boolean; why_not: string | null }[]
  sold_by: number; clouds: number; required_vram_gb: number | null
  hardware: { provider: string; cloud: string; instance: string; gpu_count: number; gpu_model: string; gpu_mem_gb: number; region: string | null
    usd_per_hour: number; usd_per_month: number; others: number
    /** set when no one server holds the model on this cloud: that many of the instance, joined by this network */
    servers?: number; cluster_network?: string }[]
  breakeven: { tokens_per_month: number; output_tokens_per_second: number; assumed_tokens_per_second: number | null; achievable: boolean
    input_output_ratio: number; cloud: string; instance: string } | null
  related: { maker: ModelLink[]; size: ModelLink[]; country: { slug: string; name: string; clouds: string[] } | null }
  collected_at: number | null
}
export interface GpuRow { provider: string; cloud: string; gpu: string; gpu_slug: string; instance: string; region: string | null
  gpu_count: number; gpu_mem_gb: number; usd_per_gpu_hour: number; usd_per_hour: number; instances: number }
export interface CloudPageData extends ExploreHead {
  kind: 'cloud'; slug: string; label: string
  models: { slug: string; label: string; input: number | null; output: number | null; region: string | null; regions: number }[]
  gpus: GpuRow[]; regions: string[]; prices: number | null; collected_at: number | null
  /** who the vendor is, written by hand with sources (pricing/data/vendors.json); null where none is written */
  profile?: VendorProfile | null
  /** the pages the vendor's serving prices were read from */
  read_from?: string[]
  /** the last hand check of this vendor's prices against its page (pricing/data/verified.json) */
  verified?: { checked_on: string; by?: string; note?: string } | null
  sells?: string[]
  countries?: string[]
  /** what each kind of price is read from (api/catalog.py price_sources) */
  sources?: { kind: string; items: { label: string; url: string | null; files?: string[]; note?: string }[] }[]
  /** where the vendor runs, in one place (api/catalog.py where_it_runs) */
  where?: VendorWhere
}
export interface VendorWhere {
  summary: string; short: string; source: 'price-list' | 'docs'
  countries: { code: string; name: string; gov: boolean; regions: string[]; models: number; gpus: number }[]
  /** OCI: where Oracle's documentation says the models run on demand, by country */
  documented: { code: string; name: string; models: number; regions: { name: string; models: number }[] }[]
  /** the models with one global price and no stated place */
  unplaced: string[]
  other_regions: string[]
  notes: string[]
  gpu_note?: string
}
export interface VendorProfile {
  kind: string[]; website: string; hq: string | null; intro: string; logo?: string
  sources: { label: string; url: string }[]
}
export interface GpuPageData extends ExploreHead {
  kind: 'gpu'; slug: string; label: string; mem_gb: number | null; rows: GpuRow[]; clouds: number
  holds: { slug: string; label: string; need_gb: number }[]; collected_at: number | null
}
export interface CountryPageData extends ExploreHead {
  kind: 'country'; slug: string; code: string; name: string; present: string[]
  clouds: { provider: string; cloud: string; regions: string[] }[]
  models: { slug: string; label: string; cells: { provider: string; cloud: string; input: number | null; output: number | null; region: string | null }[] }[]
  collected_at: number | null
}
/** What changed (api/changes.py): a month's report, one notable change, and their lists */
export interface ReportPageData extends ExploreHead {
  kind: 'report'; month: string; label: string; in_progress: boolean; updated_at: number | null; summary: string; pdf: boolean
  counts: { price_cuts: number; price_rises: number; models_sold: number; models_withdrawn: number; instances: number; regions: number
    announcements: number; catalogue: number; retiring: number }
  moves: { id: number; path: string | null; cloud: string; what: string; old: number | null; new: number | null; unit: string; pct: number; at: number }[]
  more_moves: number
  sold: { path: string | null; cloud: string; name: string; input: number | null; output: number | null; regions: string[]; model: string | null }[]
  withdrawn: { path: string | null; cloud: string; name: string }[]
  retiring: { cloud: string; model: string; on: string; replacement: string; url?: string | null; path?: string }[]
  instances: { cloud: string; count: number }[]; regions: { cloud: string; count: number }[]
  announcements: { cloud: string; title: string; url: string | null; at: number }[]
  previous: string | null; next: string | null
}
export interface ChangePageData extends ExploreHead {
  kind: 'change'; id: number; path: string; what: string; at: number; provider: string; cloud: string; headline: string; short: string; name: string
  old: number | null; new: number | null; unit: string; pct: number | null; pricing_model: string; region: string; regions: string[]; gpu: string
  input: number | null; output: number | null; retires_at: string; replacement: string; source: string | null
  history: { at: number; price: number }[]; means: string; links: { path: string; label: string }[]
}
export type ExplorePageData = ModelPageData | CloudPageData | GpuPageData | CountryPageData | ReportPageData | ChangePageData
  | (ExploreHead & { kind: 'reports'; months: { month: string; label: string; in_progress: boolean; price_cuts: number; price_rises: number; models_sold: number; models_withdrawn: number }[] })
  | (ExploreHead & { kind: 'changes'; changes: { id: number; path: string; at: number; kind: string; cloud: string; headline: string; month: string }[] })
  | (ExploreHead & { kind: 'models'; models: { slug: string; canonical: string; label: string; maker: string; priced: boolean; params_b: number | null; open_weights: boolean; clouds: number; downloads: number | null }[]; families?: FamilyCard[] })
  | (ExploreHead & FamilyCard & { kind: 'family'; vendors: number; smallest_vram_gb: number | null; rows: FamilyRow[] })
  | (ExploreHead & { kind: 'clouds'; clouds: { slug: string; label: string; models: number; gpus: number; regions: number; prices: number | null; logo?: string | null; kind?: string[]; sells?: string[]; countries?: number }[] })
  | (ExploreHead & { kind: 'countries'; countries: { slug: string; code: string; name: string; group: string; clouds: string[] }[] })
  | (ExploreHead & { kind: 'gpus'; gpus: { slug: string; label: string; clouds: string[]; mem_gb: number | null; from_usd_per_gpu_hour: number; cheapest_cloud: string }[] })

/** Admin > Automatic pages (api/auto_pages.py) */
export interface AutoPagesState {
  available: boolean
  kinds: { key: string; label: string; on: boolean; pages: number; in_sitemap: number | null; newest: { label: string; at: number; path: string } | null }[]
  lists: { key: string; label: string; path: string; on: boolean; footer: boolean; live: boolean; in_sitemap: boolean }[]
  totals: { live: number; in_sitemap: number }
  refresh: { last_at: number | null; found: number; auto: boolean; next_at: number | null }
  notable: Record<string, boolean>; min_price_move_pct: number; report_in_progress: boolean; sitemap_sizing: number
  recent: { at: number; kind: string; label: string; path: string; hide: string }[]
  hidden: { path: string; label: string }[]
  countries: { slug: string; name: string; clouds: number; on: boolean }[]
}

/** Optimise (tools.optimise): the savings ladder for a client's workloads */
export interface OptimiseUseCase {
  id: string; label: string; latency: 'interactive' | 'minutes' | 'hours'; context_tokens: number; prefix_tokens: number
  cache_hit: number; routing_share: number | null; right_size: boolean; note: string
}
export interface OptimisePriced { available: boolean; reason?: string; label: string; monthly_usd?: number[]; total_usd?: number; detail?: Record<string, unknown> }
export interface OptimiseLever {
  id: string; workload: number; lever: string; title: string; why: string; assumption: string | null
  needs_decision: boolean; default_on: boolean; priced: boolean; saves_usd: number | null
  after: { label: string; monthly_usd: number[]; total_usd: number; detail: Record<string, unknown> } | null
}
export interface Optimised {
  months: number; growth_pct: number; regions: RegionChoices
  workloads: { index: number; label: string; canonical: string; use_case: string; use_case_label: string
    today: OptimisePriced; optimised: OptimisePriced & { applied?: string[] }; levers: OptimiseLever[] }[]
  ladder: OptimiseLever[]; applied: string[]
  totals: { today_usd: number; optimised_usd: number; saving_usd: number; saving_pct: number; today_monthly_usd: number[]; optimised_monthly_usd: number[] }
  basis: string
}

/** A family on the Models page (catalog.family_index): the maker a model is filed under */
export interface FamilyCard { name: string; slug: string; models: number; priced: number; open: number; closed: number; logo: string | null }
/** One model on a family's page (catalog.family_page) */
export interface FamilyRow {
  slug: string; label: string; params_b: number | null; open_weights: boolean; priced: boolean
  vendors: string[]; input: number | null; output: number | null; cheapest: string | null; vram_gb: number | null
}

/** One vendor's reserved capacity against its own on-demand price (tools.reserved_capacity). */
export interface ReservedRow {
  provider: Provider; provider_name: string
  region_choice: string; region_label: string
  available: boolean
  /** a vendor that sells reserved capacity the site does not price yet */
  not_priced?: boolean
  reason?: string
  on_demand: { monthly_usd: number; usd_per_1m_input: number; usd_per_1m_output: number; service_tier?: string | null; region?: string | null } | null
  offer?: string; unit?: string; scope?: string; region?: string | null
  term_used?: 'none' | '1m' | '3m'; term_label?: string; term_note?: string | null
  meters?: { name: string; direction?: string; usd_per_hour?: number; usd_per_hour_per_1k_tpm?: number; usd_per_ai_unit_hour?: number; product?: string }[]
  capacity?: { azure_name: string; input_tpm_per_ptu: number; output_to_input: number; source: string; source_dated: string; read_on: string }
  note?: string; source?: string | null
  hourly_usd?: number; monthly_usd?: number
  sizing?: { units: string; input_tpm?: number; output_tpm?: number; ptus?: number; replicas?: number; raw_ptus?: number; minimum?: number; step?: number }
  /** the busy time above which reserved costs no more than on demand; null when it never does at this volume */
  breakeven_busy?: number | null
  cheaper_than_on_demand?: boolean | null
  curve?: { busy: number; monthly_usd: number }[]
}

export interface ReservedCapacity {
  canonical: string; label: string
  workload: { output_tokens_per_day: number; input_output_ratio: number; days: number; input_tokens_per_day: number; input_tpm: number; output_tpm: number }
  utilization: number
  term: 'none' | '1m' | '3m'; term_label: string
  terms: { id: 'none' | '1m' | '3m'; label: string }[]
  rows: ReservedRow[]
  cheapest_reserved: { provider: Provider; provider_name: string; monthly_usd: number } | null
  cheapest_on_demand: { provider: Provider; provider_name: string; monthly_usd: number } | null
  ptu_source: { source: string; source_dated: string; read_on: string }
  assumptions: { hours_per_month: number; minutes_per_day: number; egress: string }
}

/** Tokens a day from a count of work (tools.workload_from_tasks). */
export interface TasksWorkload {
  tasks_per_day: number; calls_per_day: number; prompt_tokens: number; answer_tokens: number; reasoning_tokens_per_call: number
  calls_per_task: number; reasoning_share: number; retry_rate: number
  input_tokens_per_day: number; output_tokens_per_day: number; input_output_ratio: number | null
}

/** A usage export read into tokens a day per model (pricing/bills.py, tools.bill_workloads). */
export interface BillReading {
  format: 'tokens' | 'azure' | 'aws' | 'oci' | 'gcp' | 'snowflake' | 'alibaba'; source: string | null; container: 'csv' | 'json'
  columns: Record<string, string>
  rows: { model: string; input_tokens: number; output_tokens: number; cached_tokens: number; blended_tokens?: number; split?: string | null; rows: number; days: number
    input_tokens_per_day: number; output_tokens_per_day: number; canonical: string | null; label: string | null; match: string | null }[]
  days: number | null; days_used: number; days_source: string
  rows_read: number; ignored: { row: number; why: string }[]; ignored_count: number; notes: string[]
}
